import { io, Socket } from 'socket.io-client';
import CookieUtils from '../utils/cookie-utils';

// Socket events
export enum SOCKET_LISTEN_EVENTS {
  JOIN_BRANCH_ROOM = 'join_branch_room',
  LEAVE_BRANCH_ROOM = 'leave_branch_room',
  SUBSCRIBE_DRIVER_PICKUP = 'subscribe_driver_pickup',
  SUBSCRIBE_DRIVER_DELIVERY = 'subscribe_driver_delivery',
  SUBSCRIBE_EMPLOYEE_LAUNDRY = 'subscribe_employee_laundry',
}

export enum SOCKET_EMIT_EVENTS {
  ORDER_CREATED = 'order_created',
  ORDER_STATUS_CHANGED = 'order_status_changed',
  ORDER_ASSIGNED = 'order_assigned',
  ORDER_CANCELLED = 'order_cancelled',
  ORDER_PAYMENT_SUCCESS = 'order_payment_success',
  NEW_ORDER_FOR_PICKUP = 'new_order_for_pickup',
  NEW_ORDER_FOR_LAUNDRY = 'new_order_for_laundry',
  NEW_ORDER_FOR_DELIVERY = 'new_order_for_delivery',
  EQUIPMENT_ASSIGNED = 'equipment_assigned',
  EQUIPMENT_RELEASED = 'equipment_released',
  // Extended Workflow (Phase 2)
  ORDER_STEP_ADVANCED = 'order_step_advanced',
  ORDER_SPLIT = 'order_split',
  NOTIFICATION = 'notification',
}

export interface OrderEventData {
  order_id: number;
  order_code: string;
  branch_id: number;
  event_type?: string;
  previous_status?: number;
  current_status: number;
  message?: string;
  order_data?: any;
  timestamp: string;
  // Computed field để tiện truy cập - backend gửi order_data
  order?: any;
  // Driver/Employee IDs
  pickup_driver_id?: number;
  delivery_driver_id?: number;
  employee_id?: number;
  actor_id?: number;
  actor_name?: string;
}

export type SocketEventCallback = (data: OrderEventData) => void;

class SocketService {
  private socket: Socket | null = null;
  private isConnecting = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private listeners: Map<string, Set<SocketEventCallback>> = new Map();
  private currentUrl: string | null = null;

  /**
   * Connect to socket server
   * @param realtimeUrl - URL của realtime service (được truyền từ SocketContext sau khi gọi PublicService.setting())
   * @param forceReconnect - Force reconnect nếu đã connected
   */
  async connect(realtimeUrl: string, forceReconnect: boolean = false): Promise<boolean> {
    // Nếu force reconnect, disconnect socket cũ trước
    if (forceReconnect && this.socket) {
      console.log('[Socket] Force reconnect - disconnecting old socket...');
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.isConnecting = false;
    }

    if (this.socket?.connected) {
      console.log('[Socket] Already connected');
      return true;
    }

    if (this.isConnecting) {
      console.log('[Socket] Connection in progress...');
      return false;
    }

    const token = CookieUtils.getCurrentUser().access_token ?? "";
    if (!token) {
      console.log('[Socket] No auth token, skipping connection');
      throw new Error('No auth token');
    }

    if (!realtimeUrl) {
      console.log('[Socket] No realtime URL provided');
      throw new Error('No realtime URL');
    }

    this.isConnecting = true;
    this.currentUrl = realtimeUrl;
    console.log('[Socket] Connecting to:', realtimeUrl);

    return new Promise((resolve, reject) => {
      this.socket = io(`${realtimeUrl}/orders`, {
        transports: ['websocket', 'polling'],
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        auth: {
          token: token,
        },
        extraHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });

      this.socket.on('connect', () => {
        console.log('[Socket] Connected successfully to:', realtimeUrl);
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.reattachListeners();
        resolve(true);
      });

      this.socket.on('connect_error', (error) => {
        console.error('[Socket] Connection error:', error.message);
        this.isConnecting = false;
        this.reconnectAttempts++;

        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          reject(new Error('Max reconnection attempts reached'));
        }
      });

      this.socket.on('disconnect', (reason) => {
        console.log('[Socket] Disconnected:', reason);
        this.isConnecting = false;
      });

      this.socket.on('reconnect', (attemptNumber) => {
        console.log('[Socket] Reconnected after', attemptNumber, 'attempts');
        this.reattachListeners();
      });
    });
  }

  /**
   * Disconnect from socket server
   */
  disconnect(): void {
    if (this.socket) {
      console.log('[Socket] Disconnecting...');
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.isConnecting = false;
    this.currentUrl = null;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Get current connected URL
   */
  getCurrentUrl(): string | null {
    return this.currentUrl;
  }

  /**
   * Subscribe to orders for specific branches
   */
  subscribeToOrders(branchIds: number[]): void {
    if (!this.socket?.connected) {
      console.warn('[Socket] Not connected, cannot subscribe');
      return;
    }

    // Subscribe to all roles for admin
    this.socket.emit(SOCKET_LISTEN_EVENTS.SUBSCRIBE_DRIVER_PICKUP, { branch_ids: branchIds }, (response: any) => {
      console.log('[Socket] Subscribed as pickup driver:', response);
    });

    this.socket.emit(SOCKET_LISTEN_EVENTS.SUBSCRIBE_DRIVER_DELIVERY, { branch_ids: branchIds }, (response: any) => {
      console.log('[Socket] Subscribed as delivery driver:', response);
    });

    this.socket.emit(SOCKET_LISTEN_EVENTS.SUBSCRIBE_EMPLOYEE_LAUNDRY, { branch_ids: branchIds }, (response: any) => {
      console.log('[Socket] Subscribed as laundry employee:', response);
    });
  }

  /**
   * Join branch room
   */
  joinBranchRoom(branchId: number): void {
    if (!this.socket?.connected) return;

    this.socket.emit(SOCKET_LISTEN_EVENTS.JOIN_BRANCH_ROOM, { branch_id: branchId }, (response: any) => {
      console.log('[Socket] Joined branch room:', response);
    });
  }

  /**
   * Leave branch room
   */
  leaveBranchRoom(branchId: number): void {
    if (!this.socket?.connected) return;

    this.socket.emit(SOCKET_LISTEN_EVENTS.LEAVE_BRANCH_ROOM, { branch_id: branchId });
  }

  /**
   * Add event listener
   */
  on(event: string, callback: SocketEventCallback): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)?.add(callback);

    if (this.socket?.connected) {
      this.socket.on(event, callback);
    }
  }

  /**
   * Remove event listener
   */
  off(event: string, callback?: SocketEventCallback): void {
    if (callback) {
      this.listeners.get(event)?.delete(callback);
      this.socket?.off(event, callback);
    } else {
      this.listeners.delete(event);
      this.socket?.off(event);
    }
  }

  /**
   * Re-attach all listeners after reconnect
   */
  private reattachListeners(): void {
    if (!this.socket) return;

    this.listeners.forEach((callbacks, event) => {
      callbacks.forEach((callback) => {
        this.socket?.on(event, callback);
      });
    });
  }
}

// Singleton instance
export const socketService = new SocketService();
export default socketService;
